/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {MaterialReactTableProps, MRT_Cell, MRT_ColumnDef} from "material-react-table";
import {selectUserState, setIsRoadCurvaturePopupVisible} from "../../../../../redux/ducks/User/slice";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import cloneDeep from "lodash/cloneDeep";
import ArgosButton from "../../../../components/Buttons/ArgosButton";
import {usePostRequest} from "../../../../../api/helper";
import ADAS_PROGRAMS_CONSTANTS from "../../constants";
import toast from "react-hot-toast";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import ArgosCollapseComponent from "../../../../components/CollapseComponent/ArgosCollapseComponent";
import {useGetIsUserAdmin, useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import RoadCurvaturePopup from "../../../../components/Dialogs/AdasProgramsDialogs/RoadCurvaturePopup";
import {StyledArgosDialog} from "../../../../components/Dialogs/ArgosDialog";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import {
  selectAdasProgramsArchive,
  selectIsVehicleProgramReadOnlyState,
} from "../../../../../redux/ducks/AdasPrograms/slice";
import {selectCurrentUser} from "../../../../../redux/ducks/AccessControl/slice";
import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";

interface IProps {
  adasProgramId: string
}

const LIST_COLUMNS = [
  {header: 'Property', id: 'property'},
  {header: 'Value', id: 'value'},
  {header: 'Units', id: 'units'},
  {header: 'Comments', id: 'comments'},
]

const EDITABLE_COLUMNS = new Set(['value', 'comments'])

enum COLUMN_TYPES {
  TEXT = 'string',
  NUMBER = 'number',
  ANY = 'any'

}

const MAP_COLUMN_IDS_TO_TYPES = new Map([
  ['property', COLUMN_TYPES.TEXT],
  ['value', COLUMN_TYPES.ANY],
  ['units', COLUMN_TYPES.TEXT],
  ['comments', COLUMN_TYPES.TEXT],
  ['pixelsAcrossShortDimension', COLUMN_TYPES.NUMBER],
])

interface IRow {
  property: string
  value: number | string
  units: string
  comments: string
}

interface IObjectRow {
  id: string
  name: string
  type: string
  pixelsAcrossShortDimension: number
  comments: string
}

const ProgramProperties = (props: IProps) => {
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const dispatch = useAppDispatch()
  const isRoadCurvaturePopupVisible = useAppSelector(state => selectUserState(state).isRoadCurvaturePopupVisible)
  const {adasProgramId} = props
  const [showFloatingList, setShowFloatingList] = useState<boolean>(false);
  const [data, setData] = React.useState<IRow[]>([]);
  const refTableData = React.useRef<any>([])
  const refDbId = React.useRef<string | null>(null)
  const [anchorElement, setAnchorElement] = useState<any>(null);
  const fetchPropertiesHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.fetchProgramPropertiesEndpoint)
  const [isFetchingSensorBomData, setIsFetchingSensorBomData] = React.useState(true)
  const [objectsData, setObjectsData] = useState<IObjectRow[]>([]);
  const [componentDataMap, setComponentDataMap] = useState<any>({})
  const [curvatureValues, setCurvatureValues] = useState<string>()
  const hasWriteAccess = useHasWriteAccess();
  const isAdmin = useGetIsUserAdmin();

  useEffect(() => {
    if (!adasProgramId) {
      return
    }
    setIsFetchingSensorBomData(true)
    fetchPropertiesHandle({adasProgramId}).then((r) => {
      if (!r) {
        return
      }
      setIsFetchingSensorBomData(false)
      setData(r?.programPropertiesTable?.tableRows)
      setObjectsData(r?.programPropertiesTable?.objectsData)
      refTableData.current = r?.programPropertiesTable
      refDbId.current = r?.dbId
    }).catch((e) => {
      setIsFetchingSensorBomData(false)
      console.error(e)
      toast.error("Failed to fetch program properties")
    }).finally(() => {
      setIsFetchingSensorBomData(false)
    })
  }, [adasProgramId]);

  const [hasSaved, setHasSaved] = React.useState(false)
  const [isSavingSensorBomData, setIsSavingSensorBomData] = React.useState(false)
  const savePropertiesHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.saveProgramPropertiesEndpoint)
  const [hasEditAccess, setHasEditAccess] = React.useState(false)

  const onSave = () => {
    const payload = {
      adasProgramId,
      programPropertiesTable: refTableData.current,
      dbId: refDbId.current,
    }
    setIsSavingSensorBomData(true)
    savePropertiesHandle(payload).then((r) => {
      setHasSaved(true)
    }).catch((e) => {
      console.error(e)
      toast.error("Failed to save program properties")
    }).finally(() => {
      setIsSavingSensorBomData(false)
    })
  }

  const roadCurvatureChanges = (updatedValue: string) => {
    refTableData.current['tableRows'][1]['value'] = updatedValue
    setCurvatureValues(updatedValue)
    setHasSaved(false)
  }

  const getCommonEditTextFieldProps = useCallback((
    cell: MRT_Cell<IRow> | MRT_Cell<IObjectRow>,
    tableToEdit: string
  ): MRT_ColumnDef<any>['muiEditTextFieldProps'] => {
    return {
      type: 'number',
      InputProps: {
        inputProps: {min: 0}
      },
      value: refTableData.current?.[tableToEdit][cell.row.index][cell.column.id],
      onChange: (e: any) => {
        e.target.value = e.target.value < 0 ? (e.target.value = 0) : e.target.value
        const colId: string = cell.column.id
        refTableData.current[tableToEdit][cell.row.index][colId] = e.target.value
        setHasSaved(false)
      },
      onBlur: (e) => {
        const _type = MAP_COLUMN_IDS_TO_TYPES.get(cell.column.id)
        refTableData.current[tableToEdit][cell.row.index][cell.column.id] = _type === COLUMN_TYPES.NUMBER ? Number(e.target.value) : e.target.value
        if (tableToEdit == "objectsData") {
          setObjectsData(cloneDeep(refTableData.current.objectsData))
        } else {
          setData(cloneDeep(refTableData.current.tableRows))
        }
      },
    };
  }, [data])

  const getCommonEditTextFieldPropsString = useCallback((
    cell: MRT_Cell<IRow> | MRT_Cell<IObjectRow>,
    tableToEdit: string
  ): MRT_ColumnDef<any>['muiEditTextFieldProps'] => {
    return {
      type: 'string',
      InputProps: {
        inputProps: {min: ''}
      },
      value: refTableData.current?.[tableToEdit][cell.row.index][cell.column.id],
      onChange: (e: any) => {
        e.target.value = e.target.value < 0 ? (e.target.value = 0) : e.target.value
        const colId: string = cell.column.id
        refTableData.current[tableToEdit][cell.row.index][colId] = e.target.value
        setHasSaved(false)
      },
      onBlur: (e) => {
        const _type = MAP_COLUMN_IDS_TO_TYPES.get(cell.column.id)
        refTableData.current[tableToEdit][cell.row.index][cell.column.id] = _type === COLUMN_TYPES.NUMBER ? Number(e.target.value) : e.target.value
        if (tableToEdit == "objectsData") {
          setObjectsData(cloneDeep(refTableData.current.objectsData))
        } else {
          setData(cloneDeep(refTableData.current.tableRows))
        }
      },
    };
  }, [data])

  useEffect(() => {
    createComponentDataMap();
  }, [data, objectsData, showFloatingList, isFetchingSensorBomData])

  const createComponentDataMap = () => {
    const _componentDataMap: any = {}
    _componentDataMap["General Properties"] =
      (<div>
        <ArgosTableSimple
          data={data}
          columns={adasProgramsPropertiesColumns}

          enablePagination={true}
          enableBottomToolbar={true}

          editDisplayMode="cell"
          enableEditing={!reduxIsProgramArchive}
          state={{isLoading: isFetchingSensorBomData}}
        />
      </div>)
    _componentDataMap["Pixels Across Short Dimension for each Object"] =
      (<div>
        <ArgosTableSimple
          data={objectsData}
          columns={objectsTableColumns}

          enablePagination={true}
          enableBottomToolbar={true}

          editDisplayMode="cell"
          enableEditing={!reduxIsProgramArchive}
          enableGrouping={true}
          state={{isLoading: isFetchingSensorBomData}}
        />
      </div>)
    setComponentDataMap(_componentDataMap);
  }

  const adasProgramsPropertiesColumns: MRT_ColumnDef<IRow>[] = useMemo<MRT_ColumnDef<IRow>[]>(
    () => [
      {
        accessorKey: 'property',
        header: 'Property',
        minSize: 80,
        size: 400,

      },
      {
        accessorKey: 'value',
        header: 'Value',
        minSize: 80,
        size: 200,
        Cell: ({cell, row}) => {
          if (row.original.property == "Typical Road Curvatures") {
            setCurvatureValues(row.original.value as string)
            return <>
              <div onClick={(e) => {
                dispatch(setIsRoadCurvaturePopupVisible(true))
              }}
              >...
              </div>
            </>
          } else {
            return <div>{cell.getValue<string>()}</div>
          }
        },
        muiEditTextFieldProps: ({cell}) => ({
          ...getCommonEditTextFieldProps(cell, "tableRows"),
          disabled: cell.row.original.property == "Typical Road Curvatures"
        }),
      },
      {
        accessorKey: 'units',
        header: 'Unit',
        minSize: 80,
        size: 200,
      },
      {
        accessorKey: 'comments',
        header: 'Comments',
        minSize: 80,
        size: 500,
        muiEditTextFieldProps: ({cell}) => ({
          ...getCommonEditTextFieldPropsString(cell, "tableRows")
        })
      },
    ],
    [data, getCommonEditTextFieldProps, showFloatingList]
  )

  const objectsTableColumns: MRT_ColumnDef<IObjectRow>[] = useMemo<MRT_ColumnDef<IObjectRow>[]>(
    () => [
      {
        accessorKey: 'type',
        id: 'objectCategory',
        header: 'Object Category',
        minSize: 80,
        size: 200,
        enableEditing: false
      },
      {
        accessorKey: 'name',
        id: 'objectName',
        header: 'Object Name',
        minSize: 80,
        size: 400,
        Cell: ({cell, row}) => {
          // return <span><a href={`/objectDetails?id=${row.original.id}`} target="_blank">{cell.getValue<string>()}</a></span>
          return <span><a href="#" onClick={() => window.open(`/objectDetails?id=${row.original.id}`, '_blank')}>{cell.getValue<string>()}</a></span>
        },
        enableEditing: false
      },
      {
        accessorKey: 'pixelsAcrossShortDimension',
        header: 'Pixels Across Short Dimension',
        minSize: 80,
        size: 200,
        editable: true,
        muiEditTextFieldProps: ({cell}) => ({
          ...getCommonEditTextFieldProps(cell, "objectsData")
        })
      },
      {
        accessorKey: 'comments',
        header: 'Comments',
        minSize: 80,
        size: 600,
        muiEditTextFieldProps: ({cell}) => ({
          ...getCommonEditTextFieldPropsString(cell, "objectsData")
        })
      }
    ],
    [data, getCommonEditTextFieldProps, getCommonEditTextFieldPropsString]
  )

  return (
    <ArgosFlexbox vertical style={{marginTop: 20}}>
      <div style={{padding: '4px 8px 4px'}}>
        <ArgosButton
          disabled={hasSaved || isFetchingSensorBomData || isSavingSensorBomData || !hasWriteAccess || reduxIsProgramArchive}
          isLoadingButton={true} isLoading={isSavingSensorBomData}
          onClick={() => {
            onSave()
          }}
        >
          <span style={{marginRight: isSavingSensorBomData ? 20 : 0}}>
            Save Changes
          </span>
        </ArgosButton>
      </div>
      <div>
        <ArgosCollapseComponent
          data={componentDataMap}
          defaultOpen={true}
        />
      </div>
      <StyledArgosDialog
        open={isRoadCurvaturePopupVisible}
        maxWidth={'40w'}
        minWidth={'40vw'}
        height={'fit-content'}
      >
        <RoadCurvaturePopup value={curvatureValues} roadCurvatureChanges={roadCurvatureChanges} editAccess={isAdmin}/>
      </StyledArgosDialog>
    </ArgosFlexbox>
  )
}

export default ProgramProperties
